import { Button } from '@design-system/components/button/button';
import { Modal, PlainModalProps } from '@design-system/components/modal/modal';
import { Typography } from '@design-system/components/typography/typography';
import { useState } from 'react';
import { sprinkles } from '@design-system/sprinkles.css';
import { clsx } from 'clsx';
import CollectionImage from '../../../../../static/images/flow-map-collection.png';
import WorkoutImage from '../../../../../static/images/flow-map-workout.png';
import LibraryImage from '../../../../../static/images/flow-map-library.png';
import { Tag } from '@design-system/components/tag/tag';
import { PATHS } from '@client/routes';
import { Link } from 'react-router';
import {
  arrowIcon,
  arrowLeft,
  arrowRight,
  card,
  cardWrapper,
  library,
  program,
  workout,
} from './flowEducationalModal.css';
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

type FlowEducationalModalProps = Pick<PlainModalProps, 'isOpen' | 'onClose'> & {
  source: 'library' | 'workout' | 'program';
  influencerId: number;
  programId?: number;
};

export const FlowEducationalModal = (p: FlowEducationalModalProps) => (
  <Modal
    {...p}
    modalClassName="w-full h-full md:w-[80%] md:h-[80%]"
    contentClassName="w-full h-full gap-0"
  >
    <Content {...p} />
  </Modal>
);

const Content = (p: FlowEducationalModalProps) => {
  switch (p.source) {
    case 'library':
      return <Library {...p} />;
    case 'workout':
      return <Workout {...p} />;
    case 'program':
      return <Program {...p} />;
  }
};

const StepCount = ({ step }: { step: number }) => (
  <Typography variant="body" size="large" className="absolute t-0 l-0">
    {step}/2
  </Typography>
);

const Library = (props: FlowEducationalModalProps) => {
  const [step, setStep] = useState<'1' | '2'>('1');
  switch (step) {
    case '1':
      return (
        <div className="w-full h-full overflow-hidden">
          <StepCount step={1} />
          <div className="w-full h-full flex flex-col gap-6 justify-center items-center">
            <Typography variant="display" size="small">
              Your content is all connected ✨
            </Typography>
            <Typography variant="body" size="large" fontWeight="normal">
              Understanding the setup is key—it helps you work more efficiently
              and start earning faster.
            </Typography>
            <FlowMap source={props.source} />
            <Button
              color="neutral"
              className="mt-6"
              onClick={() => setStep('2')}
            >
              Next
            </Button>
          </div>
        </div>
      );

    case '2':
      return (
        <div className="w-full h-full relative">
          <StepCount step={2} />
          <div className="w-full h-full flex flex-col gap-6 justify-center items-center">
            <Typography variant="display" size="small">
              Want to create quickly?
            </Typography>
            <Typography variant="body" size="large" fontWeight="normal">
              This video will teach you about the most efficient way to load up
              your Library.
            </Typography>
            <iframe
              src="https://www.loom.com/embed/522713b630bf4fa5ac1b4834304648b0?sid=13141f47-9b13-41bd-97f5-32e583532f81"
              className="bg-black w-full h-[200px] md:w-[700px] md:h-[360px] rounded-[20px]"
            ></iframe>
            <div className="text-center">
              <Typography
                variant="body"
                size="large"
                fontWeight="normal"
                as="span"
              >
                Psst! Have you tried uploading videos directly through the
              </Typography>
              <a
                href="https://apps.apple.com/pl/app/playbook-creator/id1166632935"
                target="_blank"
                className="underline pl-1"
                rel="noreferrer"
              >
                <Typography
                  variant="body"
                  size="large"
                  fontWeight="normal"
                  color="P-80"
                  as="span"
                >
                  mobile app
                </Typography>
              </a>
              <Typography
                fontWeight="normal"
                variant="body"
                size="large"
                as="span"
              >
                ? Give it a shot! 🤓
              </Typography>
            </div>
            <div className="flex flex-col md:flex-row justify-center items-center gap-8">
              <Button
                color="neutral"
                variant="text"
                onClick={() => setStep('1')}
              >
                Back
              </Button>
              <Button color="neutral" onClick={props.onClose}>
                Let’s start
              </Button>
            </div>
          </div>
        </div>
      );
  }
};

const Workout = (props: FlowEducationalModalProps) => (
  <div className="w-full h-full flex flex-col gap-6 justify-center items-center">
    <Typography variant="display" size="small">
      Let’s build your workouts!
    </Typography>
    <Typography variant="body" size="large">
      Combine workouts out of uploaded clips & videos.
    </Typography>
    <FlowMap source={props.source} />
    <div className="flex items-center">
      <Typography variant="body" size="large" fontWeight="normal">
        Psst! Have you tried uploading videos directly through the
      </Typography>
      <a
        href="https://apps.apple.com/pl/app/playbook-creator/id1166632935"
        target="_blank"
        rel="noreferrer"
      >
        <Typography
          variant="body"
          size="large"
          fontWeight="normal"
          color="P-80"
          className="underline pl-1"
        >
          mobile app
        </Typography>
      </a>
      <Typography fontWeight="normal" variant="body" size="large">
        ? Give it a shot! 🤓
      </Typography>
    </div>
    <Link
      to={PATHS.WORKOUT_EDIT({
        workoutId: 0,
        influencerId: props.influencerId,
        programId: props.programId,
      })}
    >
      <Button color="neutral" className="justify-self-end">
        Build a workout
      </Button>
    </Link>
  </div>
);

const Program = (props: FlowEducationalModalProps) => (
  <div className="w-full h-full flex flex-col gap-6 justify-center items-center">
    <Typography variant="display" size="small">
      It’s time to build a Program 🤓
    </Typography>
    <Typography variant="body" size="large">
      Let's continue by combining your workouts into a Program.
    </Typography>
    <FlowMap source={props.source} />
    <div
      className={clsx(
        'flex flex-col gap-2 p-2 rounded-[10px]',
        sprinkles({ bg: 'P-20' }),
      )}
    >
      <Typography variant="body" size="small" fontWeight="bold">
        PRO TIP 🎯
      </Typography>
      <div className="flex items-center">
        <Typography variant="body" size="large" fontWeight="normal">
          Build 2-3 weeks and duplicate. Best practice is a 4 to 6 weeks
          program.{' '}
        </Typography>
        <a
          href="https://help.playbookapp.io/en/articles/9478481-how-the-pros-create-a-successful-program"
          target="_blank"
          rel="noreferrer"
        >
          <Typography
            variant="body"
            size="large"
            fontWeight="normal"
            color="B-100"
            className="pl-1 underline"
          >
            Learn more
          </Typography>
        </a>
      </div>
    </div>
    <Button
      color="neutral"
      className="justify-self-end"
      onClick={props.onClose}
    >
      Make a program
    </Button>
  </div>
);

const flowMap = [
  { source: 'library', url: LibraryImage },
  { source: 'workout', url: WorkoutImage },
  { source: 'program', url: CollectionImage },
];

const handleChangeView = (
  currentStep: FlowEducationalModalProps['source'],
  direction: 'left' | 'right',
): FlowEducationalModalProps['source'] => {
  if (direction === 'left') {
    switch (currentStep) {
      case 'library':
        return 'library';
      case 'workout':
        return 'library';
      case 'program':
        return 'workout';
    }
  }
  switch (currentStep) {
    case 'library':
      return 'workout';
    case 'workout':
      return 'program';
    case 'program':
      return 'program';
  }
};

const FlowMap = ({
  source,
}: {
  source: FlowEducationalModalProps['source'];
}) => {
  const [view, setView] = useState(source);
  return (
    <div className={clsx('group', card)}>
      <div
        className={clsx(
          arrowLeft,
          'invisible',
          view !== 'library' && 'group-hover:visible',
        )}
        onClick={() => setView(handleChangeView(view, 'left'))}
      >
        <FontAwesomeIcon icon={faChevronLeft} className={arrowIcon} />
      </div>
      <div
        className={clsx(
          cardWrapper,
          view === 'library' && library,
          view === 'workout' && workout,
          view === 'program' && program,
        )}
      >
        {flowMap.map((item) => (
          <div
            key={item.source}
            className={clsx(
              'rounded-[13px] flex w-[325px] h-[238px] items-center justify-center relative',
              source === item.source &&
                sprinkles({ borderColor: 'G-100', borderWidth: 's' }),
            )}
          >
            {source === item.source && (
              <Tag
                label="📍 You are here"
                className={clsx(
                  sprinkles({ bg: 'G-100', color: 'W-100' }),
                  'absolute bottom-[-15px] right-2 uppercase',
                )}
                readonly
              />
            )}
            <img src={item.url} className="w-full h-full" />
          </div>
        ))}
      </div>
      <div
        className={clsx(
          arrowRight,
          'invisible',
          view !== 'program' && 'group-hover:visible',
        )}
        onClick={() => setView(handleChangeView(view, 'right'))}
      >
        <FontAwesomeIcon icon={faChevronRight} className={arrowIcon} />
      </div>
    </div>
  );
};
